import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { DecisionNumberObject } from '@libs/types/src/services/object-type/cssz/decision-number.object';
import { GetServiceInput, OkObject } from '@it2go/types';
import { GetByInsuranceNumberInput } from '@libs/types/src/services/input-type/cssz/get-by-insurance-number.input';
import { EmployersObject } from '@libs/types/src/services/object-type/cssz/employers.object';
import { employersFrag, folderSickNoteFrag, folderOtherSickNoteListFrag } from './types/folder-sick-notes.types';
import { FolderSickNotesCreateInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.create-input';
import { GridInput } from '@it2go/data-grid'
import { filterFrag } from './types/filter.types';
import { FolderSickNotesGridObject } from '@libs/types/src/services/object-type/folder/folder-sick-notes.object';
import { FolderSickNotesUpdateInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.update-input';
import { SickNotesObject } from '@libs/types/src/services/object-type/cssz/sick-notes.object';
import { TakeOverSickNoteInput } from '@libs/types/src/services/input-type/cssz/take-over-sick-note.input';
import { FolderSickNotesHolInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.hol-input';
import { FolderSickNotesPtdpnInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.ptdpn-input';
import { FolderSickNotesCloseInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.close-input';
import { FolderSickNotesZolusouInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.zolusou-input';
import { FolderSickNotesZolusuInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.zolusu-input';
import { FolderSickNotesZpnpusInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.zpnpus-input';
import { FolderSickNotesZuspvyInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.zuspvy-input';

@Injectable({ providedIn: 'root' })
export class FolderSickNotesGqlService extends GqlServiceV2 {

    getDecisionNumber(input: GetServiceInput) {
        return this.query<DecisionNumberObject>(
            `
                query($input: GetServiceInput!) {
                    folderSickNotes {
                        getDecisionNumber(input: $input) {
                            number
                        }
                    }
                }
            `,
            { input },
        );
    }

    getEmployers(input: GetByInsuranceNumberInput, frag = employersFrag) {
        return this.query<EmployersObject>(
            `
                query($input: GetByInsuranceNumberInput!) {
                    folderSickNotes {
                        getEmployers(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getOtherSickNotes(input: GetByInsuranceNumberInput, frag: string) {
        return this.query<SickNotesObject>(
            `
                query($input: GetByInsuranceNumberInput!) {
                    folderSickNotes {
                        getOtherSickNotes(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getSickNotes(input: GridInput | null, frag = folderSickNoteFrag) {
        return this.query<FolderSickNotesGridObject>(
            `
                query($input: GridInput) {
                    folderSickNotes {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createSickNote(input: FolderSickNotesCreateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesCreateInput!) {
                    folderSickNotes {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateSickNote(input: FolderSickNotesUpdateInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesUpdateInput!) {
                    folderSickNotes {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateHol(input: FolderSickNotesHolInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesHolInput!) {
                    folderSickNotes {
                        updateHol(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updatePtdpn(input: FolderSickNotesPtdpnInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesPtdpnInput!) {
                    folderSickNotes {
                        updatePtdpn(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateZolusou(input: FolderSickNotesZolusouInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesZolusouInput!) {
                    folderSickNotes {
                        updateZolusou(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateZolusu(input: FolderSickNotesZolusuInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesZolusuInput!) {
                    folderSickNotes {
                        updateZolusu(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateZpnpus(input: FolderSickNotesZpnpusInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesZpnpusInput!) {
                    folderSickNotes {
                        updateZpnpus(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateZuspvy(input: FolderSickNotesZuspvyInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesZuspvyInput!) {
                    folderSickNotes {
                        updateZuspvy(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    takeOver(input: TakeOverSickNoteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: TakeOverSickNoteInput!) {
                    folderSickNotes {
                        takeOver(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    close(input: FolderSickNotesCloseInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderSickNotesCloseInput!) {
                    folderSickNotes {
                        close(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

}
